import { useState } from "react";
import { Button } from "antd";
import { Download } from "lucide-react";
import jsPDF from "jspdf";
import QrCodeLogo from "../assets/images/duepay-logo-white.png";
import QrDownloadModal from "./QrDownloadModal";

const loadImage = (src) =>
    new Promise((resolve, reject) => {
        if (!src) {
            return reject(new Error("Image source is required"));
        }

        // Data URLs and Blob URLs don't need crossOrigin
        if (src.startsWith("data:") || src.startsWith("blob:")) {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = () => reject(new Error("Failed to load inline image data"));
            img.src = src;
            return;
        }

        const tryLoadWithImage = (url, onFail) => {
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.onload = () => resolve(img);
            img.onerror = onFail;
            img.src = url;
        };

        // 1. Initial attempt with crossOrigin="anonymous"
        tryLoadWithImage(src, () => {
            // 2. Retry with cache-busting timestamp to bypass cached non-CORS response
            const separator = src.includes("?") ? "&" : "?";
            const cacheBustedUrl = `${src}${separator}_cb=${Date.now()}`;

            tryLoadWithImage(cacheBustedUrl, () => {
                // 3. Fallback: fetch as blob and load from object URL
                fetch(src, { mode: "cors" })
                    .then((res) => {
                        if (!res.ok) throw new Error(`HTTP error ${res.status}`);
                        return res.blob();
                    })
                    .then((blob) => {
                        const blobUrl = URL.createObjectURL(blob);
                        const blobImg = new Image();
                        blobImg.onload = () => {
                            URL.revokeObjectURL(blobUrl);
                            resolve(blobImg);
                        };
                        blobImg.onerror = () => {
                            URL.revokeObjectURL(blobUrl);
                            reject(new Error(`Failed to decode image from ${src}`));
                        };
                        blobImg.src = blobUrl;
                    })
                    .catch((err) => {
                        reject(
                            new Error(
                                `Failed to load image from "${src}". Possible CORS restriction or network error: ${err?.message || err}`
                            )
                        );
                    });
            });
        });
    });

const roundedRect = (ctx, x, y, width, height, radius) => {
    const r = Math.min(radius, width / 2, height / 2);
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + width, y, x + width, y + height, r);
    ctx.arcTo(x + width, y + height, x, y + height, r);
    ctx.arcTo(x, y + height, x, y, r);
    ctx.arcTo(x, y, x + width, y, r);
    ctx.closePath();
};

const getAvatarUrl = (user = {}) => {
    const u = user?.users || user;
    return (
        u?.profile_img ||
        u?.profile_image ||
        u?.business_logo ||
        u?.profile_photo ||
        u?.avatar ||
        u?.photo_url ||
        user?.profile_img ||
        user?.business_logo ||
        ""
    );
};

const getDisplayName = (user = {}) => {
    const u = user?.users || user;
    if (user?.business_name) return user.business_name;
    if (u?.business_name) return u.business_name;
    if (user?.full_name) return user.full_name;
    if (u?.full_name) return u.full_name;

    const parts = [
        u?.first_name || user?.first_name,
        u?.middle_name || user?.middle_name,
        u?.last_name || user?.last_name,
    ].filter(Boolean);

    if (parts.length > 0) {
        return parts.join(" ").trim();
    }

    return "DuePay User";
};

const getDisplaySubtitle = (user = {}, payId) => {
    const u = user?.users || user;
    const countryCode = u?.country_code || user?.country_code || "";
    const mobile = u?.mobile || user?.mobile || u?.phone || user?.phone || "";
    if (mobile) {
        if (countryCode && !mobile.startsWith("+") && !mobile.startsWith(countryCode)) {
            return `${countryCode} ${mobile}`.trim();
        }
        return mobile;
    }
    if (payId && payId !== "N/A") {
        return payId;
    }
    return "";
};

export const PAPER_SIZES = {
    a5: { widthMm: 148, heightMm: 210, label: "A5", pxWidth300Dpi: 1748, pxHeight300Dpi: 2480 },
    a6: { widthMm: 105, heightMm: 148, label: "A6", pxWidth300Dpi: 1240, pxHeight300Dpi: 1748 },
    a4: { widthMm: 210, heightMm: 297, label: "A4", pxWidth300Dpi: 2480, pxHeight300Dpi: 3508 },
};

// CRC32 table & calculation for PNG metadata chunks
const makeCrcTable = () => {
    let c;
    const table = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
        c = n;
        for (let k = 0; k < 8; k++) {
            c = (c & 1) ? (0xedb88320 ^ (c >>> 1)) : (c >>> 1);
        }
        table[n] = c;
    }
    return table;
};

let crcTable = null;
const crc32 = (bytes) => {
    if (!crcTable) crcTable = makeCrcTable();
    let crc = 0 ^ (-1);
    for (let i = 0; i < bytes.length; i++) {
        crc = (crc >>> 8) ^ crcTable[(crc ^ bytes[i]) & 0xff];
    }
    return (crc ^ (-1)) >>> 0;
};

/**
 * Embeds physical pixel dimensions (pHYs chunk) into PNG binary
 * At 300 DPI: Math.round(300 / 0.0254) = 11811 pixels per meter (unit = 1 meter)
 */
export const embedPngDpi = (pngDataUrl, dpi = 300) => {
    const base64Data = pngDataUrl.replace(/^data:image\/png;base64,/, "");
    const binaryString = atob(base64Data);
    const pngBytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
        pngBytes[i] = binaryString.charCodeAt(i);
    }

    // Pixels per meter (1 meter = 39.3701 inches)
    const dpm = Math.round(dpi / 0.0254);
    const physData = new Uint8Array(9);
    const view = new DataView(physData.buffer);
    view.setUint32(0, dpm);
    view.setUint32(4, dpm);
    view.setUint8(8, 1); // 1 = meter

    // Chunk = 4 bytes length + 4 bytes 'pHYs' + 9 bytes data + 4 bytes CRC
    const chunkTypeAndData = new Uint8Array(13);
    // 'p' = 0x70, 'H' = 0x48, 'Y' = 0x59, 's' = 0x73
    chunkTypeAndData.set([0x70, 0x48, 0x59, 0x73], 0);
    chunkTypeAndData.set(physData, 4);

    const crc = crc32(chunkTypeAndData);
    const physChunk = new Uint8Array(21);
    const chunkView = new DataView(physChunk.buffer);
    chunkView.setUint32(0, 9); // length = 9 bytes data
    physChunk.set(chunkTypeAndData, 4);
    chunkView.setUint32(17, crc); // crc

    // Validate PNG signature [0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A]
    if (
        pngBytes.length < 8 ||
        pngBytes[0] !== 0x89 ||
        pngBytes[1] !== 0x50 ||
        pngBytes[2] !== 0x4e ||
        pngBytes[3] !== 0x47
    ) {
        return new Blob([pngBytes], { type: "image/png" });
    }

    let offset = 8;
    const chunks = [];
    let hasPhys = false;

    while (offset < pngBytes.length) {
        if (offset + 8 > pngBytes.length) break;
        const length =
            (pngBytes[offset] << 24) |
            (pngBytes[offset + 1] << 16) |
            (pngBytes[offset + 2] << 8) |
            pngBytes[offset + 3];
        const chunkType = String.fromCharCode(
            pngBytes[offset + 4],
            pngBytes[offset + 5],
            pngBytes[offset + 6],
            pngBytes[offset + 7]
        );
        const chunkTotalLen = 12 + length;
        if (offset + chunkTotalLen > pngBytes.length) break;
        const chunk = pngBytes.slice(offset, offset + chunkTotalLen);

        if (chunkType === "pHYs") {
            chunks.push(physChunk);
            hasPhys = true;
        } else {
            chunks.push(chunk);
            if (chunkType === "IHDR" && !hasPhys) {
                chunks.push(physChunk);
                hasPhys = true;
            }
        }
        offset += chunkTotalLen;
    }

    const totalLength = 8 + chunks.reduce((acc, c) => acc + c.length, 0);
    const result = new Uint8Array(totalLength);
    result.set(pngBytes.slice(0, 8), 0);
    let cur = 8;
    for (const c of chunks) {
        result.set(c, cur);
        cur += c.length;
    }

    return new Blob([result], { type: "image/png" });
};

export const generateQrCanvas = async ({ qrCodeUrl, user = {}, payId, size = "a5", dpi = 300 }) => {
    if (!qrCodeUrl || qrCodeUrl === "N/A") {
        throw new Error("Missing QR code URL");
    }

    const targetSize = (size || "a5").toLowerCase();
    const sizeConfig = PAPER_SIZES[targetSize] || PAPER_SIZES.a5;
    const targetDpi = dpi || 300;

    // Calculate canvas pixel dimensions for the chosen paper size at target DPI (300 DPI)
    // E.g. A5 (148 x 210 mm) at 300 DPI = 1748 x 2480 px
    // E.g. A4 (210 x 297 mm) at 300 DPI = 2480 x 3508 px
    const targetWidth = Math.round((sizeConfig.widthMm / 25.4) * targetDpi);
    const targetHeight = Math.round((sizeConfig.heightMm / 25.4) * targetDpi);

    const canvas = document.createElement("canvas");
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext("2d");

    if (!ctx) {
        throw new Error("Could not generate QR template");
    }

    // Enable high-quality bicubic smoothing for ultra-crisp 300 DPI rendering
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    // Virtual base design dimensions
    const width = 1080;
    const height = 1528;

    // Scale context so design coordinates map onto high-resolution 300 DPI canvas
    const scaleX = targetWidth / width;
    const scaleY = targetHeight / height;
    ctx.scale(scaleX, scaleY);

    const qrImage = await loadImage(qrCodeUrl);
    const brandImage = await loadImage(QrCodeLogo).catch(() => null);
    const avatarUrl = getAvatarUrl(user);
    const avatarImage = avatarUrl ? await loadImage(avatarUrl).catch(() => null) : null;

    // 1. Full-bleed background gradient
    const gradient = ctx.createLinearGradient(0, 0, 0, height);
    gradient.addColorStop(0, "#003B7C");
    gradient.addColorStop(0.5, "#004B99");
    gradient.addColorStop(1, "#0073DA");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    // 2. White DuePay logo on top
    if (brandImage) {
        const brandWidth = 225;
        const brandHeight = (brandImage.height / brandImage.width) * brandWidth;
        const brandX = (width - brandWidth) / 2;
        const brandY = 95;
        ctx.drawImage(brandImage, brandX, brandY, brandWidth, brandHeight);
    }

    // 3. SCAN TO PAY
    ctx.fillStyle = "#FFFFFF";
    ctx.font = "700 36px Arial, Helvetica, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    if ("letterSpacing" in ctx) {
        ctx.letterSpacing = "4px";
    }
    ctx.fillText("SCAN TO PAY", width / 2, 298);
    if ("letterSpacing" in ctx) {
        ctx.letterSpacing = "0px";
    }

    // 4. White card
    const cardX = 140;
    const cardY = 390;
    const cardWidth = 800;
    const cardHeight = 960;
    const cardRadius = 56;
    roundedRect(ctx, cardX, cardY, cardWidth, cardHeight, cardRadius);
    ctx.fillStyle = "#FFFFFF";
    ctx.fill();

    // 5. Inner light-blue container for QR
    const innerX = 180;
    const innerY = 430;
    const innerWidth = 720;
    const innerHeight = 690;
    const innerRadius = 36;
    roundedRect(ctx, innerX, innerY, innerWidth, innerHeight, innerRadius);
    ctx.fillStyle = "#F8FAFD";
    ctx.fill();
    ctx.strokeStyle = "#E2EEFD";
    ctx.lineWidth = 3;
    ctx.stroke();

    // White box inside inner container
    const qrBoxX = 220;
    const qrBoxY = 470;
    const qrBoxWidth = 640;
    const qrBoxHeight = 610;
    roundedRect(ctx, qrBoxX, qrBoxY, qrBoxWidth, qrBoxHeight, 24);
    ctx.fillStyle = "#FFFFFF";
    ctx.fill();

    // Draw QR image
    const qrSize = 548;
    const qrX = (width - qrSize) / 2;
    const qrY = 501;
    ctx.drawImage(qrImage, qrX, qrY, qrSize, qrSize);

    // 6. Account type badge pill
    const u = user?.users || user;
    const isBusiness = !!(user?.business_name || u?.business_name);
    const accountLabel = isBusiness ? "BUSINESS ACCOUNT" : "PERSONAL ACCOUNT";
    ctx.font = "700 20px Arial, Helvetica, sans-serif";
    const textMetrics = ctx.measureText(accountLabel);
    const badgePaddingX = 28;
    const badgeWidth = Math.max(320, Math.ceil(textMetrics.width + badgePaddingX * 2));
    const badgeHeight = 48;
    const badgeX = (width - badgeWidth) / 2;
    const badgeY = 1140;
    roundedRect(ctx, badgeX, badgeY, badgeWidth, badgeHeight, 24);
    ctx.fillStyle = "#0E4399";
    ctx.fill();

    ctx.fillStyle = "#FFFFFF";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    if ("letterSpacing" in ctx) {
        ctx.letterSpacing = "1.5px";
    }
    ctx.fillText(accountLabel, width / 2, badgeY + badgeHeight / 2);
    if ("letterSpacing" in ctx) {
        ctx.letterSpacing = "0px";
    }

    // 7. Avatar & User info row
    const name = getDisplayName(user);
    const subtitle = getDisplaySubtitle(user, payId);

    const avatarRadius = 55;
    const avatarDiameter = avatarRadius * 2;
    const avatarCenterX = 255;
    const avatarCenterY = 1255;
    const avatarX = avatarCenterX - avatarRadius;
    const avatarY = avatarCenterY - avatarRadius;

    if (avatarImage) {
        ctx.save();
        ctx.beginPath();
        ctx.arc(avatarCenterX, avatarCenterY, avatarRadius, 0, Math.PI * 2);
        ctx.clip();
        ctx.drawImage(avatarImage, avatarX, avatarY, avatarDiameter, avatarDiameter);
        ctx.restore();

        ctx.beginPath();
        ctx.arc(avatarCenterX, avatarCenterY, avatarRadius, 0, Math.PI * 2);
        ctx.strokeStyle = "#E2E8F0";
        ctx.lineWidth = 2;
        ctx.stroke();
    } else {
        ctx.beginPath();
        ctx.arc(avatarCenterX, avatarCenterY, avatarRadius, 0, Math.PI * 2);
        ctx.fillStyle = "#0E4399";
        ctx.fill();
        ctx.fillStyle = "#FFFFFF";
        ctx.font = "700 38px Arial, Helvetica, sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        const initials = name
            .split(" ")
            .filter(Boolean)
            .slice(0, 2)
            .map((part) => part[0]?.toUpperCase())
            .join("");
        ctx.fillText(initials || "DP", avatarCenterX, avatarCenterY);
    }

    // Name & Subtitle
    const textX = 330;
    const maxTextWidth = cardX + cardWidth - textX - 30;

    ctx.textAlign = "left";
    ctx.textBaseline = "alphabetic";

    // Name
    ctx.fillStyle = "#0F172A";
    ctx.font = "700 36px Arial, Helvetica, sans-serif";
    let displayName = name.toUpperCase();
    if (ctx.measureText(displayName).width > maxTextWidth) {
        while (displayName.length > 3 && ctx.measureText(displayName + "...").width > maxTextWidth) {
            displayName = displayName.slice(0, -1);
        }
        displayName += "...";
    }
    ctx.fillText(displayName, textX, 1246);

    // Phone / Pay ID
    if (subtitle) {
        ctx.fillStyle = "#475569";
        ctx.font = "500 26px Arial, Helvetica, sans-serif";
        let displaySub = subtitle;
        if (ctx.measureText(displaySub).width > maxTextWidth) {
            while (displaySub.length > 3 && ctx.measureText(displaySub + "...").width > maxTextWidth) {
                displaySub = displaySub.slice(0, -1);
            }
            displaySub += "...";
        }
        ctx.fillText(displaySub, textX, 1286);
    }

    // 8. Bottom text "Pay with DuePay"
    ctx.fillStyle = "#FFFFFF";
    ctx.font = "700 46px Arial, Helvetica, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "alphabetic";
    ctx.fillText("Pay with DuePay", width / 2, 1460);

    const fileName = `DuePay_QR`;
    return { canvas, name, fileName, targetWidth, targetHeight, dpi: targetDpi };
};

export const downloadQrAsPng = async ({ qrCodeUrl, user, payId, size = "a5", dpi = 300 }) => {
    if (!qrCodeUrl || qrCodeUrl === "N/A") return;
    const targetSize = (size || "a5").toLowerCase();
    const { canvas } = await generateQrCanvas({ qrCodeUrl, user, payId, size: targetSize, dpi });

    // Render canvas and embed 300 DPI metadata in PNG pHYs chunk
    const rawDataUrl = canvas.toDataURL("image/png");
    const blobWithDpi = embedPngDpi(rawDataUrl, dpi || 300);
    const objectUrl = URL.createObjectURL(blobWithDpi);

    const sizeUpper = targetSize.toUpperCase();
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = `DuePay_QR_${sizeUpper}_${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
};

export const downloadQrAsPdf = async ({ qrCodeUrl, user, payId, size = "a5", dpi = 300 }) => {
    if (!qrCodeUrl || qrCodeUrl === "N/A") return;
    const targetSize = (size || "a5").toLowerCase();
    const { canvas } = await generateQrCanvas({ qrCodeUrl, user, payId, size: targetSize, dpi });

    const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: targetSize,
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const imgData = canvas.toDataURL("image/png");
    // Full bleed 300 DPI: edge-to-edge covering entire page exactly as in reference PDFs
    doc.addImage(imgData, "PNG", 0, 0, pageWidth, pageHeight, undefined, "FAST");
    const sizeUpper = targetSize.toUpperCase();
    doc.save(`DuePay_QR_${sizeUpper}_${Date.now()}.pdf`);
};

export const downloadQrTemplate = async ({ qrCodeUrl, user, payId, format = "png", size = "a5", dpi = 300 }) => {
    if (!qrCodeUrl || qrCodeUrl === "N/A") return;
    if (format === "pdf") {
        return await downloadQrAsPdf({ qrCodeUrl, user, payId, size, dpi });
    }
    return await downloadQrAsPng({ qrCodeUrl, user, payId, size, dpi });
};

const QrCode = ({ qrCodeUrl, user, payId }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);

    return (
        <>
            <div
                style={{ position: "relative", width: 150, height: 150 }}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <img
                    src={qrCodeUrl}
                    crossOrigin="anonymous"
                    alt="QR Code"
                    style={{ width: 150, height: 150, display: "block" }}
                />

                {isHovered && (
                    <div
                        style={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            background: "rgba(0, 0, 0, 0.15)",
                        }}
                    >
                        <Button
                            shape="circle"
                            className="qr-code-download-button"
                            icon={<Download size={16} />}
                            onClick={() => setIsModalOpen(true)}
                        />
                    </div>
                )}
            </div>

            <QrDownloadModal
                open={isModalOpen}
                visible={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                qrCodeUrl={qrCodeUrl}
                user={user}
                payId={payId}
                zIndex={2000}
                downloadPng={(size) => downloadQrAsPng({ qrCodeUrl, user, payId, size })}
                downloadPdf={(size) => downloadQrAsPdf({ qrCodeUrl, user, payId, size })}
            />
        </>
    );
};

export { QrDownloadModal };
export default QrCode;